import timestring from 'timestring';

/**
 * Meant to be used with react-form's `setValueAs` option.
 * Map an input value to a duration `${number}s` or the original value in the case that it is invalid.
 * We need to return the original invalid value to avoid clobbering an invalid input in the Input
 *
 * @param rawValue Raw input value to convert to a duration value or return the original value
 */
export function mapStringToDurationOrOriginal<T extends string | null>(rawValue: T): `${number}s` | T {
  try {
    if (!rawValue) {
      return rawValue;
    }
    return parseDuration(rawValue);
  } catch (e) {
    if (e instanceof Error && e.message === INVALID_ERR) {
      return rawValue;
    }
    throw e;
  }
}

export const INVALID_ERR = 'Please input a valid duration';

export function parseDuration(input: string): `${number}s` {
  // input must begin with digits, an optional period, and otherwise contain whitespace and alphanumeric chars
  if (!/^[0-9]+(?:.)?[\s|\d|a-z|A-Z]*$/.test(input)) {
    throw new Error(INVALID_ERR);
  }
  try {
    const roundedSeconds = Math.round(
      // if the input is only numbers, add an `s` to the end for seconds
      new RegExp(/[a-zA-Z]+/).test(input) ? timestring(input) : timestring(`${input}s`)
    );
    return `${roundedSeconds}s`;
  } catch (e) {
    if (
      e instanceof Error &&
      (new RegExp(/The unit \[.{0,255}\] is not supported by timestring/).test(e.message) ||
        new RegExp(/The string \[.{0,255}\] could not be parsed by timestring/).test(e.message) ||
        new RegExp(/The value \[.{0,255}\] could not be parsed by timestring/).test(e.message))
    ) {
      throw new Error(INVALID_ERR);
    }
    throw e;
  }
}
